/**
 * TRUS-709 (Integrated Inbox, phase 3): the member Inbox list, owners /
 * responders, the "someone else replied" signal and the org messaging settings.
 *
 * The backend ships in parallel, so every reader here is tolerant: a missing
 * endpoint (404 / 405) resolves to `null` (callers fall back to the older
 * surfaces) and missing fields normalise to empty values instead of throwing.
 */
import { apiClient, handleApiError } from './apiClient';
import type { Channel } from './messagingApi';

// ---------------------------------------------------------------- types

export interface StaffRef {
  user_id: number;
  name: string;
}

export interface Responder extends StaffRef {
  last_replied_at: string | null;
}

export interface StaffReply extends StaffRef {
  at: string | null;
}

/** View chips of the member Inbox. `unrouted` is admins-only in caseload mode. */
export type InboxView = 'all' | 'mine' | 'unassigned' | 'needs_reply' | 'unrouted';
export type InboxChannelFilter = 'sms' | 'email' | 'portal' | 'call';

export interface MemberConversation {
  channel_id: number;
  member_id: number | null;
  member_name: string;
  preview: string;
  last_activity_at: string | null;
  unread_count: number;
  /** Channel of the last message (portal / sms / email / call / ...). */
  last_channel: string | null;
  owners: StaffRef[];
  responders: Responder[];
  last_staff_reply: StaffReply | null;
  last_inbound_at: string | null;
  needs_reply: boolean;
  /** TRUS-745: the caller pinned this conversation (absent on older backends). */
  is_pinned?: boolean;
  pin_position?: number | null;
}

export interface MemberConversationPage {
  items: MemberConversation[];
  next_cursor: string | null;
}

export interface MemberConversationQuery {
  view?: InboxView;
  channel?: InboxChannelFilter | null;
  program_id?: number | null;
  q?: string;
  cursor?: string | null;
  limit?: number;
  /** TRUS-745: only the caller's pinned conversations, in pin order (one page). */
  pinned?: boolean;
}

export type InboxMemberVisibility = 'team' | 'caseload';

export interface MessagingSettings {
  inbox_member_visibility: InboxMemberVisibility;
  show_staff_names_to_members: boolean;
  /** Present when the server says whether the caller may PATCH. */
  can_edit?: boolean;
  [key: string]: unknown;
}

export type OwnersMode = 'add' | 'replace';

// ---------------------------------------------------------------- normalisers

function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

/** A 404 / 405: the endpoint is not deployed yet. */
export function isMissingEndpoint(error: unknown): boolean {
  const status = statusOf(error);
  return status === 404 || status === 405;
}

function num(v: unknown): number | null {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null;
}

export function normalizeStaffRef(raw: unknown): StaffRef | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = num(r.user_id ?? r.id);
  if (id == null) return null;
  return { user_id: id, name: str(r.name) ?? str(r.user_name) ?? str(r.display_name) ?? '' };
}

export function normalizeStaffRefs(raw: unknown): StaffRef[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<number>();
  const out: StaffRef[] = [];
  for (const item of raw) {
    const ref = normalizeStaffRef(item);
    if (ref && !seen.has(ref.user_id)) {
      seen.add(ref.user_id);
      out.push(ref);
    }
  }
  return out;
}

export function normalizeResponders(raw: unknown): Responder[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      const ref = normalizeStaffRef(item);
      if (!ref) return null;
      const r = item as Record<string, unknown>;
      return { ...ref, last_replied_at: str(r.last_replied_at) ?? str(r.at) };
    })
    .filter((r): r is Responder => r !== null);
}

export function normalizeStaffReply(raw: unknown): StaffReply | null {
  const ref = normalizeStaffRef(raw);
  if (!ref) return null;
  const r = raw as Record<string, unknown>;
  return { ...ref, at: str(r.at) ?? str(r.last_replied_at) ?? str(r.created_at) };
}

export function normalizeStaffReplies(raw: unknown): StaffReply[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeStaffReply).filter((r): r is StaffReply => r !== null);
}

export function normalizeConversation(raw: unknown): MemberConversation | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const channelId = num(r.channel_id ?? r.id);
  if (channelId == null) return null;
  const member = (r.member && typeof r.member === 'object' ? r.member : {}) as Record<string, unknown>;
  return {
    channel_id: channelId,
    member_id: num(r.member_id ?? r.dm_member_id ?? member.id),
    member_name:
      str(r.member_name) ?? str(member.name) ?? str(r.display_name) ?? str(r.name) ?? '',
    preview: str(r.preview) ?? str(r.last_message_preview) ?? '',
    last_activity_at:
      str(r.last_activity_at) ?? str(r.last_message_at) ?? str(r.updated_at) ?? null,
    unread_count: num(r.unread_count) ?? 0,
    last_channel: str(r.last_channel) ?? str(r.last_message_channel) ?? str(r.channel) ?? null,
    owners: normalizeStaffRefs(r.owners),
    responders: normalizeResponders(r.responders),
    last_staff_reply: normalizeStaffReply(r.last_staff_reply),
    last_inbound_at: str(r.last_inbound_at),
    needs_reply: r.needs_reply === true,
    is_pinned: r.is_pinned === true,
    pin_position: num(r.pin_position),
  };
}

function normalizePage(data: unknown): MemberConversationPage {
  const d = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  const list = Array.isArray(data)
    ? data
    : Array.isArray(d.items)
      ? d.items
      : Array.isArray(d.conversations)
        ? d.conversations
        : Array.isArray(d.results)
          ? d.results
          : [];
  const items = (list as unknown[])
    .map(normalizeConversation)
    .filter((c): c is MemberConversation => c !== null);
  const next = d.next_cursor ?? d.cursor ?? null;
  return { items, next_cursor: typeof next === 'string' && next ? next : next != null ? String(next) : null };
}

/**
 * The fallback list when the member Inbox endpoint is not deployed: member
 * DMs from the channel list, shaped like Inbox rows (no owners / responders).
 */
export function conversationFromChannel(ch: Channel): MemberConversation {
  const extra = ch as Channel & Record<string, unknown>;
  return {
    channel_id: ch.id,
    member_id: ch.dm_member_id ?? null,
    member_name: ch.display_name || ch.name || '',
    preview: ch.last_message_preview || '',
    last_activity_at: ch.last_message_at,
    unread_count: ch.unread_count || 0,
    last_channel: str(extra.last_channel),
    owners: normalizeStaffRefs(extra.owners),
    responders: normalizeResponders(extra.responders),
    last_staff_reply: normalizeStaffReply(extra.last_staff_reply),
    last_inbound_at: str(extra.last_inbound_at),
    needs_reply: extra.needs_reply === true,
  };
}

export function normalizeSettings(data: unknown): MessagingSettings {
  const outer = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  const d = (
    outer.settings && typeof outer.settings === 'object' ? outer.settings : outer
  ) as Record<string, unknown>;
  const visibility = d.inbox_member_visibility === 'caseload' ? 'caseload' : 'team';
  const canEdit = outer.can_edit ?? d.can_edit;
  return {
    ...d,
    inbox_member_visibility: visibility,
    show_staff_names_to_members: d.show_staff_names_to_members !== false,
    ...(typeof canEdit === 'boolean' ? { can_edit: canEdit } : {}),
  };
}

// ---------------------------------------------------------------- API

export const memberInboxApi = {
  /**
   * The member Inbox (one row per member DM). `null` when the endpoint is not
   * deployed yet: the caller falls back to member DMs from the channel list.
   */
  async listMemberConversations(query: MemberConversationQuery = {}): Promise<MemberConversationPage | null> {
    const params: Record<string, string | number> = {};
    if (query.view && query.view !== 'all') params.view = query.view;
    if (query.channel) params.channel = query.channel;
    if (query.program_id != null) params.program_id = query.program_id;
    if (query.q && query.q.trim()) params.q = query.q.trim();
    if (query.cursor) params.cursor = query.cursor;
    if (query.pinned) params.pinned = 'true';
    params.limit = Math.min(Math.max(query.limit ?? 50, 1), 100);
    try {
      const response = await apiClient.get('/api/messaging/inbox/member-conversations', { params });
      return normalizePage(response.data);
    } catch (error) {
      if (isMissingEndpoint(error)) return null;
      throw handleApiError(error);
    }
  },

  /** Org messaging settings. `null` when the endpoint is not deployed (hide the section). */
  async getSettings(): Promise<MessagingSettings | null> {
    try {
      const response = await apiClient.get('/api/messaging/settings');
      return normalizeSettings(response.data);
    } catch (error) {
      if (isMissingEndpoint(error)) return null;
      throw handleApiError(error);
    }
  },

  /** Org admins only. Sends just the keys that changed; the server merges them. */
  async updateSettings(patch: Partial<Pick<MessagingSettings, 'inbox_member_visibility' | 'show_staff_names_to_members'>>): Promise<MessagingSettings> {
    try {
      const response = await apiClient.patch('/api/messaging/settings', patch);
      return normalizeSettings(response.data);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Add owners to a member DM, or replace them. Returns the owners the server
   * reports, or `null` when the response carries none (the caller refetches).
   * Throws the raw error so callers can read `owner_cannot_see_member` / 404.
   */
  async setOwners(channelId: number, userIds: number[], mode: OwnersMode): Promise<StaffRef[] | null> {
    const response = await apiClient.post(`/api/messaging/channels/${channelId}/owners`, {
      user_ids: userIds,
      mode,
    });
    const data = response.data as Record<string, unknown> | null;
    return data && Array.isArray(data.owners) ? normalizeStaffRefs(data.owners) : null;
  },

  async removeOwner(channelId: number, userId: number): Promise<void> {
    await apiClient.delete(`/api/messaging/channels/${channelId}/owners/${userId}`);
  },

  /**
   * Staff (other than the caller) who replied after `since`. `null` when the
   * endpoint is not deployed; the thread then works it out from its messages.
   */
  async getActivity(channelId: number, since: string): Promise<StaffReply[] | null> {
    try {
      const response = await apiClient.get(`/api/messaging/channels/${channelId}/activity`, {
        params: { since },
      });
      const data = (response.data || {}) as Record<string, unknown>;
      return normalizeStaffReplies(data.other_staff_replies_since);
    } catch (error) {
      if (isMissingEndpoint(error)) return null;
      throw handleApiError(error);
    }
  },
};

/** The error code the owners endpoint returns for staff who cannot see the member. */
export function ownerErrorCode(error: unknown): string | null {
  const data = (error as { response?: { data?: unknown } })?.response?.data as Record<string, unknown> | undefined;
  if (!data) return null;
  const detail = data.detail as unknown;
  if (typeof data.code === 'string') return data.code;
  if (detail && typeof detail === 'object' && typeof (detail as Record<string, unknown>).code === 'string') {
    return (detail as Record<string, string>).code;
  }
  if (typeof detail === 'string' && detail.includes('owner_cannot_see_member')) return 'owner_cannot_see_member';
  return null;
}

export { statusOf as errorStatusOf };
